import {
    FlexColumn,
    FlexRowBetween,
    PageHeader,
} from 'components'
import {
    GetCompanyTemplateList,
    GetCustomerList,
    GetPendingApprovalReports,
    GetUserList,
} from 'hooks'
import {
    ApprovalsList,
    APPROVAL_STATUS,
    applyApprovalClientFilters,
    buildApprovalListFilter,
    CounterStrip,
    enrichApprovalReportsWithUsers,
    FilterRow,
    PageSubtitle,
    ScreenContainer,
    StatusPill,
} from 'page-components/report-approval'
import { useMemo, useState } from 'react'
import { Form, Message } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import {
    FormDateField,
    FormDropdownField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import { DatePickerTypes, isApprovalAdmin } from 'utility'
import { useAuth } from 'context'

const defaultFormValues = {
    StartDate: null,
    EndDate: null,
    CustomerId: '',
    TemplateId: '',
    UserId: '',
    Search: '',
}

const toDropdownOptions = options =>
    (options || []).map(opt => ({
        key: String(opt.value),
        value: opt.value,
        text: opt.text,
    }))

export const ReportApprovalsListScreen = () => {
    const { user } = useAuth()
    // admins see every list; an approver who is not an admin (e.g. the quality head as Viewer, a department head) only
    // the reports waiting for them - the server refuses the other filters
    const approvalAdmin = isApprovalAdmin(user)
    const initialPill = approvalAdmin
        ? APPROVAL_STATUS.Pending
        : APPROVAL_STATUS.AwaitingMe
    const {
        data,
        counters,
        loading,
        error,
        filter,
        totalCount,
        pageChanged,
        filterChanged,
    } = GetPendingApprovalReports(initialPill)
    const { options: customerOptionsRaw } = GetCustomerList()
    const { options: templateOptionsRaw } = GetCompanyTemplateList()
    const { options: userOptionsRaw } = GetUserList()

    const [statusPill, setStatusPill] = useState(initialPill)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: defaultFormValues,
    })

    const startDateValue = watch('StartDate')

    const customerOptions = useMemo(
        () => toDropdownOptions(customerOptionsRaw),
        [customerOptionsRaw],
    )
    const templateOptions = useMemo(
        () => toDropdownOptions(templateOptionsRaw),
        [templateOptionsRaw],
    )
    const userOptions = useMemo(
        () => toDropdownOptions(userOptionsRaw),
        [userOptionsRaw],
    )

    const listItems = useMemo(() => {
        const enriched = enrichApprovalReportsWithUsers(data, userOptions)
        return applyApprovalClientFilters(enriched, filter, templateOptions)
    }, [data, userOptions, filter, templateOptions])

    const hasExtraFilters = !!(
        filter.CustomerId ||
        filter.TemplateId ||
        filter.UserId ||
        filter.StartDate ||
        filter.EndDate ||
        filter.Search
    )
    const displayTotalCount = hasExtraFilters ? listItems.length : totalCount

    const resolveStatusForPill = pill => {
        if (pill === 'approved_this_month') return APPROVAL_STATUS.Approved
        return pill
    }

    const handleStatusPill = pill => {
        setStatusPill(pill)
        filterChanged({
            ...buildApprovalListFilter(
                getValues(),
                resolveStatusForPill(pill),
            ),
        })
    }

    const submit = formData => {
        filterChanged(
            buildApprovalListFilter(
                formData,
                resolveStatusForPill(statusPill),
            ),
        )
    }

    const resetForm = () => {
        reset(defaultFormValues)
        filterChanged(
            buildApprovalListFilter(
                defaultFormValues,
                resolveStatusForPill(statusPill),
            ),
        )
    }

    return (
        <ScreenContainer>
            <FlexRowBetween className="mb-3 align-items-start">
                <div>
                    <PageHeader header="Report Approvals" />
                    <PageSubtitle>
                        Completed reports awaiting your review. Once approved, a
                        report is locked and a certificate PDF is generated.
                    </PageSubtitle>
                </div>
            </FlexRowBetween>

            {error && <Message negative>{error}</Message>}

            <CounterStrip>
                <div className="counter">
                    <div className="num">{counters.pending ?? 0}</div>
                    <div className="label">
                        {approvalAdmin ? 'Pending' : 'Awaiting me'}
                    </div>
                </div>
                {approvalAdmin && (
                    <div className="counter">
                        <div className="num">
                            {counters.approvedThisMonth ?? 0}
                        </div>
                        <div className="label">Approved this month</div>
                    </div>
                )}
            </CounterStrip>

            {approvalAdmin && (
            <FilterRow className="mb-3">
                <StatusPill
                    type="button"
                    $active={statusPill === APPROVAL_STATUS.Pending}
                    onClick={() => handleStatusPill(APPROVAL_STATUS.Pending)}
                >
                    Pending
                </StatusPill>
                <StatusPill
                    type="button"
                    $active={statusPill === 'approved_this_month'}
                    onClick={() => handleStatusPill('approved_this_month')}
                >
                    Approved this month
                </StatusPill>
                <StatusPill
                    type="button"
                    $active={statusPill === APPROVAL_STATUS.All}
                    onClick={() => handleStatusPill(APPROVAL_STATUS.All)}
                >
                    All
                </StatusPill>
                <StatusPill
                    type="button"
                    $active={statusPill === APPROVAL_STATUS.AwaitingMe}
                    onClick={() => handleStatusPill(APPROVAL_STATUS.AwaitingMe)}
                >
                    Awaiting me
                </StatusPill>
                <StatusPill
                    type="button"
                    $active={statusPill === APPROVAL_STATUS.AwaitingOthers}
                    onClick={() =>
                        handleStatusPill(APPROVAL_STATUS.AwaitingOthers)
                    }
                >
                    Awaiting others
                </StatusPill>
                <StatusPill
                    type="button"
                    $active={statusPill === APPROVAL_STATUS.Rejected}
                    onClick={() => handleStatusPill(APPROVAL_STATUS.Rejected)}
                >
                    Rejected
                </StatusPill>
            </FilterRow>
            )}

            <div className="">
                <Form noValidate>
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            name="Search"
                            label="Search"
                            placeholder="Search by audit name or auditor..."
                            errors={errors.Search}
                        />
                        <FormDateField
                            name="StartDate"
                            control={control}
                            label="From date"
                            errors={errors.StartDate}
                            type={DatePickerTypes.Single}
                        />
                        <FormDateField
                            name="EndDate"
                            control={control}
                            label="To date"
                            disabledBefore={startDateValue}
                            errors={errors.EndDate}
                            type={DatePickerTypes.Single}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="CustomerId"
                            label="Customer"
                            placeholder="All customers"
                            errors={errors.CustomerId}
                            options={customerOptions}
                            defaultValue=""
                        />
                        <FormDropdownField
                            control={control}
                            name="TemplateId"
                            label="Template"
                            placeholder="All templates"
                            errors={errors.TemplateId}
                            options={templateOptions}
                            defaultValue=""
                        />
                        <FormDropdownField
                            control={control}
                            name="UserId"
                            label="Auditor"
                            placeholder="All auditors"
                            errors={errors.UserId}
                            options={userOptions}
                            defaultValue=""
                        />
                    </Form.Group>
                    <div className="pt-1">
                        <SubmitCancelButtons
                            loading={loading}
                            onSubmit={handleSubmit(submit)}
                            onCancel={resetForm}
                            className=""
                        />
                    </div>
                </Form>
            </div>

            <FlexColumn className="my-3">
                <ApprovalsList
                    items={listItems}
                    loading={loading}
                    totalCount={displayTotalCount}
                    page={filter.PageNumber}
                    pageSize={filter.PageSize}
                    onPageChange={pageChanged}
                />
            </FlexColumn>
        </ScreenContainer>
    )
}
